/* eslint-disable no-undef */
import { h, render } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IconLookRight, IconMagnifyingGlass } from 'utils/icon';

const fetchOutfits = async (productId, total = null) => {
  const apiRoot = 'https://widget-api.stylitics.com';
  const response = await fetch(
    `${apiRoot}/api/outfits?username=tuckernuck&item_number=${productId}${
      total !== null ? `&total=${total}` : ''
    }`,
  );
  if (!response.ok) throw new Error('Network response was not ok');
  return response.json();
};

const fetchCompleteTheLookData = async (productDetail) => {
  const { references } = productDetail;
  if (!references) {
    return [];
  }

  return references.nodes.map((node) => {
    const primaryImageField = node.fields.find(
      (field) => field.key === 'primary_image',
    );
    const badgeTextField = node.fields.find(
      (field) => field.key === 'badge_text',
    );
    const productNameField = node.fields.find(
      (field) => field.key === 'product_name',
    );

    const productsField = node.fields.find((field) => field.key === 'products');

    const primaryImage = primaryImageField?.reference?.image?.url || '';
    const badgeText = badgeTextField?.value || '';

    const products = productsField?.references?.nodes || [];

    const productDetails = products.map((productNode) => ({
      id: productNode.id.match(/\d+/)[0],
      title: productNameField,
      handle: productNode.handle,
      image:
        productNode.featuredImage?.url ||
        productNode.featuredImage?.originalSrc ||
        '',
    }));

    return {
      primaryImage,
      productNameField,
      products: productDetails,
      badgeText,
    };
  });
};

const Slider = ({ data, sectionEl }) => {
  const {
    initialProductId,
    lookHeading,
    handleProduct,
    completeTheLookData: initialCompleteTheLookData,
    badgeOutfits,
    stylisticsOutfitsCount,
    layout,
    lazyload,
  } = data;
  const [completeTheLookData, setCompleteTheLookData] = useState(
    initialCompleteTheLookData,
  );
  const [styliticsOutfits, setStyliticsOutfits] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(layout.isAboveBreakpoint('lg'));
  const [hideNavigationArrows, setHideNavigationArrows] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(initialProductId);
  const [insights, setInsights] = useState(null);
  const observerRef = useRef(null);

  const handleBpChange = () => {
    setIsDesktop(layout.isAboveBreakpoint('lg'));
  };
  window.addEventListener('bpChange', handleBpChange);

  const totalOutfits = styliticsOutfits.length + completeTheLookData.length;
  let shouldHideNavigationArrows = false;
  if (isDesktop) {
    shouldHideNavigationArrows = totalOutfits < 4;
  } else {
    shouldHideNavigationArrows = totalOutfits === 1;
  }
  setHideNavigationArrows(shouldHideNavigationArrows);

  const loadOutfits = async (productId) => {
    try {
      const outfits = await fetchOutfits(productId, stylisticsOutfitsCount);
      outfits.forEach((outfit) => {
        outfit.items.sort((a, b) => {
          if (Number(a.remote_id) === Number(productId)) {
            return -1;
          }
          if (Number(b.remote_id) === Number(productId)) {
            return 1;
          }
          return 0;
        });
      });
      setStyliticsOutfits(outfits);
      setIsLoaded(true);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  useEffect(() => {
    loadOutfits(currentProductId);
  }, [currentProductId]);

  useEffect(() => {
    const handleSwatchChange = async (event) => {
      const { id, handle } = event.detail.product;
      setCurrentProductId(id);
      try {
        const updatedCompleteTheLookData = await fetchCompleteTheLookData(
          handle,
        );
        setCompleteTheLookData(updatedCompleteTheLookData);
      } catch (error) {
        console.error('Failed to update completeTheLookData:', error);
      }
    };

    window.addEventListener('swatch:change', handleSwatchChange);

    return () => {
      window.removeEventListener('swatch:change', handleSwatchChange);
    };
  }, []);

  const handleBundleClick = (event) => {
    const clickedRemoteId = event.currentTarget.getAttribute('data-bundle-id');
    const bundleIndex = event.currentTarget.getAttribute('data-bundle-index');
    const slotIndex = event.currentTarget.getAttribute('data-slot-index');

    if (!styliticsOutfits || !bundleIndex) {
      return;
    }

    const foundBundle = styliticsOutfits[bundleIndex];
    if (!foundBundle) {
      return;
    }

    const mainProduct = foundBundle.items.find(
      (item) => item.remote_id === foundBundle.primary_remote_id,
    );

    const clickedIndex = foundBundle.items.findIndex(
      (item) => item.remote_id === clickedRemoteId,
    );

    const productHandles = foundBundle.items.map(
      (item) => item.affiliate_link.split('/products/')[1],
    );

    window.dispatchEvent(
      new CustomEvent('openLookAdd', {
        detail: {
          products: productHandles,
          selectedIndex: clickedIndex,
          stylitics: true,
          slotIndex,
          id: mainProduct.item_id,
          name: `stylitics:${handleProduct}:${bundleIndex}`,
        },
      }),
    );
    insights.trackOutfitClick({
      outfit: foundBundle,
      bundleIndex,
      uiComponent: 'collage',
    });
    insights.trackItemView({
      item: foundBundle.items[clickedIndex],
      itemImageType: 'laydown',
      position: clickedIndex,
    });
    insights.trackItemClick({
      item: foundBundle.items[clickedIndex],
      placement: 'main',
      position: clickedIndex,
    });
  };

  const handleManualBundleClick = (event) => {
    const clickedRemoteId = event.currentTarget.getAttribute('data-bundle-id');
    const bundleIndex = event.currentTarget.getAttribute('data-bundle-index');
    const slotIndex = event.currentTarget.getAttribute('data-slot-index');

    if (!completeTheLookData.length || !bundleIndex) {
      return;
    }

    const foundBundle = completeTheLookData[bundleIndex];

    if (!foundBundle) {
      return;
    }

    const mainProduct = foundBundle.products[0];

    const clickedIndex = foundBundle.products.findIndex(
      (item) => item.id === clickedRemoteId,
    );

    const productHandles = foundBundle.products.map((item) => item.handle);

    window.dispatchEvent(
      new CustomEvent('openLookAdd', {
        detail: {
          products: productHandles,
          selectedIndex: clickedIndex,
          stylitics: true,
          slotIndex,
          id: mainProduct.id,
          name: `stylitics:${handleProduct}:${bundleIndex}`,
        },
      }),
    );
  };

  useEffect(() => {
    const initializeStylitics = async () => {
      const insightsInstance = await StyliticsInsights.create({
        client: 'tuckernuck',
        itemId: `${currentProductId}`,
      });
      setInsights(insightsInstance);
    };

    initializeStylitics();
  }, []);

  useEffect(() => {
    if (insights && styliticsOutfits.length) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = entry.target.dataset.bundleIndex;
              if (!index) {
                return;
              }
              const item = styliticsOutfits[index];
              if (insights) {
                insights.trackOutfitView({
                  outfit: item,
                  outfit_index: index,
                });
              }
              observerRef.current.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 },
      );
    }

    const slides = document.querySelectorAll('.stylitics-outfit-slide');
    slides.forEach((slide) => observerRef.current.observe(slide));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [styliticsOutfits, insights]);

  if (!isLoaded) return null;

  if (!styliticsOutfits.length && !completeTheLookData.length) {
    sectionEl.classList.add('hidden');
    return null;
  }

  window.addEventListener('stylitics:item-click', (event) => {
    const { product } = event.detail;
    const itemId = product.id.split('/Product/')[1];

    const bundle = styliticsOutfits.find((outfit) =>
      outfit.items.some((item) => item.remote_id === itemId),
    );
    const bundleItemIndex = bundle.items.findIndex(
      (item) => item.remote_id === itemId,
    );

    insights.trackItemView({
      item: bundle.items[bundleItemIndex],
      itemImageType: 'client_original_image',
      position: bundleItemIndex,
    });
    insights.trackItemClick({
      item: bundle.items[bundleItemIndex],
      placement: 'main',
      position: bundleItemIndex,
    });
  });

  window.addEventListener('stylitics:add-to-cart', (event) => {
    const { product } = event.detail;
    const itemId = product.id.split('/Product/')[1];
    const bundle = styliticsOutfits.find((outfit) =>
      outfit.items.some((item) => item.remote_id === itemId),
    );

    if (!bundle) {
      return;
    }

    const bundleItemIndex = bundle.items.findIndex(
      (item) => item.remote_id === itemId,
    );
    const item = bundle.items[bundleItemIndex];
    insights.trackAddItemToCart({
      item_id: item.item_id,
      placement: 'main',
      price: item.price,
    });
  });

  if (!insights) {
    return null;
  }

  if (styliticsOutfits.length) {
    insights.trackPageVisit();
  }

  return (
    <div className="relative px-5 overflow-hidden">
      {lookHeading && (
        <h2 className="mb-5 md:mb-8 text-center md:px-0">{lookHeading}</h2>
      )}
      <Swiper
        slidesPerView={1.4}
        centerInsufficientSlides={true}
        spaceBetween={6}
        breakpoints={{
          1024: {
            slidesPerView: 3,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            spaceBetween: 45,
            centerInsufficientSlides: true,
          },
        }}
        navigation={{
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev',
          disabledClass: '!opacity-50',
        }}
        className="overflow-hidden !-mr-5 md:!mx-0"
      >
        {completeTheLookData.map((item, index) => {
          const firstItem = item.products[0];
          const remainingItems = item.products.slice(1, 5);
          const itemLazyLoading = lazyload || index > 2;

          return (
            <SwiperSlide key={`manual-${index}`}>
              <div className="grid grid-cols-[4fr_1fr] gap-[5px] cursor-pointer">
                <div
                  className="relative min-h-[279px] md:min-h-[507px]"
                  data-bundle-id={firstItem.id}
                  data-bundle-index={index}
                  data-slot-index={index + 1}
                  onClick={handleManualBundleClick}
                >
                  <img
                    src={item.primaryImage}
                    alt={firstItem.title}
                    loading={lazyload ? 'lazy' : 'eager'}
                    fetchPriority={itemLazyLoading ? 'auto' : 'high'}
                    className="w-full h-full object-cover aspect-[2/3]"
                  />
                  {item.badgeText && (
                    <div className="absolute pointer-events-none bg-white/80 md:bg-white/95 right-5 bottom-5 px-3 py-1.5 md:px-3.5 body-3 text-text-default">
                      {item.badgeText}
                    </div>
                  )}
                  <div className="flex justify-center items-center w-3 h-3 rounded-full bg-white absolute bottom-2.5 left-2.5 z-1 md:w-5 md:h-5">
                    <IconMagnifyingGlass />
                  </div>
                </div>

                <div className="grid grid-rows-4 gap-[5px]">
                  {remainingItems.map((subItem) => (
                    <div
                      key={subItem.id}
                      className="relative bg-[#F3F3F3]"
                      data-bundle-id={subItem.id}
                      data-bundle-index={index}
                      data-slot-index={index + 1}
                      onClick={handleManualBundleClick}
                    >
                      <img
                        src={subItem.image}
                        alt={subItem.title}
                        loading={lazyload ? 'lazy' : 'eager'}
                        fetchPriority={itemLazyLoading ? 'auto' : 'high'}
                        className="w-full h-full object-cover aspect-[2/3]"
                      />
                      <div className="flex justify-center items-center w-3 h-3 rounded-full bg-white absolute bottom-2.5 left-2.5 z-1 md:w-5 md:h-5">
                        <IconMagnifyingGlass />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
        {styliticsOutfits.map((item, index) => {
          const firstItem = item.items[0];
          const remainingItems = item.items.slice(1, 5);
          const itemLazyLoading =
            lazyload || index + (completeTheLookData?.length || 0) > 2;

          return (
            <SwiperSlide
              key={index}
              data-bundle-index={index}
              data-slot-index={(completeTheLookData?.length || 0) + index + 1}
              className="stylitics-outfit-slide"
            >
              <div className="grid grid-cols-[4fr_1fr] gap-[5px] cursor-pointer">
                <div
                  className="relative min-h-[279px] md:min-h-[507px] bg-[#F3F3F3]"
                  data-bundle-id={firstItem.remote_id}
                  data-bundle-index={index}
                  data-slot-index={
                    (completeTheLookData?.length || 0) + index + 1
                  }
                  onClick={handleBundleClick}
                >
                  <img
                    src={firstItem.lookbook_image_url}
                    alt={firstItem.name}
                    loading={itemLazyLoading ? 'lazy' : 'eager'}
                    fetchPriority={itemLazyLoading ? 'auto' : 'high'}
                    className="w-full h-full object-contain aspect-[2/3] p-9"
                  />
                  {badgeOutfits && (
                    <div className="absolute pointer-events-none bg-white/80 md:bg-white/95 right-5 bottom-5 px-3 py-1.5 md:px-3.5 body-3 text-text-default">
                      {badgeOutfits}
                    </div>
                  )}
                  <div className="flex justify-center items-center w-3 h-3 rounded-full bg-white absolute bottom-2.5 left-2.5 z-1 md:w-5 md:h-5">
                    <IconMagnifyingGlass />
                  </div>
                </div>

                <div className="grid grid-rows-4 gap-[5px]">
                  {remainingItems.map((subItem) => (
                    <div
                      key={subItem.remote_id}
                      className="relative bg-[#F3F3F3]"
                      data-bundle-id={subItem.remote_id}
                      data-bundle-index={index}
                      data-slot-index={
                        (completeTheLookData?.length || 0) + index + 1
                      }
                      onClick={handleBundleClick}
                    >
                      <img
                        src={subItem.lookbook_image_url}
                        alt={subItem.name}
                        loading={itemLazyLoading ? 'lazy' : 'eager'}
                        fetchPriority={itemLazyLoading ? 'auto' : 'high'}
                        className="w-full h-full object-contain aspect-[2/3] p-2"
                      />
                      <div className="flex justify-center items-center w-3 h-3 rounded-full bg-white absolute bottom-2.5 left-2.5 z-1 md:w-5 md:h-5">
                        <IconMagnifyingGlass />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div
        className={`
          absolute flex items-center md:gap-3 top-0 right-6 md:right-[107px]
          ${hideNavigationArrows ? '!opacity-0' : ''}
        `}
      >
        <button className="z-1 swiper-button--prev flex items-center rotate-180 justify-center rounded-full bg-white w-7 h-7 md:border border-border-gray md:w-10 md:h-10">
          <IconLookRight classes="stroke-2 md:stroke-1" />
        </button>
        <button className="z-1 swiper-button--next flex items-center justify-center rounded-full bg-white w-7 h-7 md:border border-border-gray md:w-10 md:h-10">
          <IconLookRight classes="stroke-2 md:stroke-1" />
        </button>
      </div>
    </div>
  );
};

export default (element, data) => {
  render(<Slider sectionEl={element} data={data} />, element);
};
